import React, { useState, useCallback, useEffect } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { FileUpload } from '../ui/FileUpload'
import { 
  Upload,
  FileText,
  X,
  CheckCircle
} from 'lucide-react'

interface UploadScopeModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { projectName: string; files: File[]; coverLetterOnly: boolean }) => void | Promise<void>
  isLoading?: boolean
  initialFiles?: File[]
}

export const UploadScopeModal: React.FC<UploadScopeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  initialFiles = []
}) => {
  const [projectName, setProjectName] = useState('')
  const [file, setFile] = useState<File | null>(initialFiles?.[0] || null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [coverLetterOnly, setCoverLetterOnly] = useState(false)

  // Update file when initialFiles changes
  useEffect(() => {
    setFile(initialFiles?.[0] || null)
  }, [initialFiles])

  const handleFileDrop = useCallback((droppedFiles: File[]) => {
    // Only accept the first file
    if (droppedFiles.length > 0) {
      setFile(droppedFiles[0])
    }
  }, [])

  const handleFileRemove = () => {
    setFile(null)
  }

  const handleSubmit = async () => {
    if (projectName.trim() && file) {
      await onSubmit({ projectName: projectName.trim(), files: [file], coverLetterOnly })
      setProjectName('')
      setFile(null)
      setCoverLetterOnly(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const droppedFiles = Array.from(e.dataTransfer.files)
    handleFileDrop(droppedFiles)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="lg">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Upload Scope</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Project Name Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Name
          </label>
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="Enter project name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {/* File Upload Area */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload RFP Document
          </label>
          
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragOver
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              Drag & drop your file here
            </p>
            <p className="text-sm text-gray-500 mb-4">
              or browse to choose file
            </p>
            <Button
              variant="outline"
              onClick={() => {
                const input = document.createElement('input')
                input.type = 'file'
                input.accept = '.pdf,.doc,.docx'
                input.onchange = (e) => {
                  const target = e.target as HTMLInputElement
                  if (target.files && target.files[0]) {
                    setFile(target.files[0])
                  }
                }
                input.click()
              }}
            >
              Browse File
            </Button>
          </div>
        </div>

        {/* Uploaded File */}
        {file && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Uploaded File
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FileText className="w-8 h-8 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <button
                    onClick={handleFileRemove}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cover Letter Only Checkbox */}
        <div className="mb-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="cover_letter_only"
              checked={coverLetterOnly}
              onChange={(e) => setCoverLetterOnly(e.target.checked)}
              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
            />
            <label htmlFor="cover_letter_only" className="ml-2 text-sm font-medium text-gray-900">
              Cover letter requirements only
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!projectName.trim() || !file || isLoading}
            className="bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-300"
          >
            {isLoading ? 'Creating Project...' : 'Create Project'}
          </Button>
        </div>
      </div>
    </Modal>
  )
} 